<template>
  <v-dialog
    :value="!!attachment"
    max-width="720"
    scrollable
    @input="(v) => !v && $emit('close')"
  >
    <v-card v-if="attachment" class="viewer-card">
      <!-- Header -->
      <v-card-title class="viewer-header">
        <v-icon :color="iconColour" size="20" class="mr-2">{{ iconForKind }}</v-icon>
        <div class="viewer-header-text">
          <div class="viewer-filename">{{ attachment.filename }}</div>
          <div class="viewer-meta">{{ metaLabel }}</div>
        </div>
        <v-spacer />
        <v-btn icon small @click="$emit('close')">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Body: extracted text. whitespace-pre-wrap preserves line breaks
           from the original document. -->
      <v-card-text class="viewer-body">
        <pre class="viewer-text">{{ attachment.text }}</pre>
      </v-card-text>

      <!-- Honest footer about what the user is looking at. -->
      <v-card-actions class="viewer-footer">
        <span class="viewer-footnote">
          This is the extracted text the assistant received — the original file isn't stored server-side.
        </span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'AttachmentViewer',
  props: {
    attachment: { type: Object, default: null },
  },
  emits: ['close'],
  setup(props) {
    const iconForKind = computed(() => {
      if (!props.attachment) return 'mdi-file-document-outline';
      if (props.attachment.kind === 'pdf') return 'mdi-file-pdf-box';
      if (props.attachment.kind === 'docx') return 'mdi-file-word-box';
      return 'mdi-file-document-outline';
    });

    const iconColour = computed(() => {
      if (!props.attachment) return 'grey darken-1';
      if (props.attachment.kind === 'pdf') return 'red darken-2';
      if (props.attachment.kind === 'docx') return 'blue darken-2';
      return 'grey darken-2';
    });

    const metaLabel = computed(() => {
      if (!props.attachment) return '';
      const parts = [props.attachment.kind.toUpperCase()];
      if (props.attachment.page_count !== null && props.attachment.page_count !== undefined) {
        const n = props.attachment.page_count;
        parts.push(`${n} page${n === 1 ? '' : 's'}`);
      }
      if (props.attachment.size_bytes > 0) {
        parts.push(formatSize(props.attachment.size_bytes));
      }
      return parts.join(' · ');
    });

    function formatSize(bytes) {
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    }

    return { iconForKind, iconColour, metaLabel };
  },
});
</script>

<style scoped>
.viewer-card {
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}
.viewer-header {
  display: flex;
  align-items: center;
  padding: 12px 20px !important;
  border-bottom: 1px solid rgb(229 231 235);
}
.viewer-header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.viewer-filename {
  font-size: 14px;
  font-weight: 600;
  color: rgb(17 24 39);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 520px;
}
.viewer-meta {
  font-size: 11px;
  color: rgb(107 114 128);
  font-weight: normal;
  text-transform: none;
}
.viewer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px !important;
}
.viewer-text {
  font-family: inherit;
  font-size: 13px;
  line-height: 1.65;
  color: rgb(31 41 55);
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
}
.viewer-footer {
  padding: 8px 20px !important;
  border-top: 1px solid rgb(229 231 235);
  background: rgb(249 250 251);
}
.viewer-footnote {
  font-size: 11px;
  color: rgb(107 114 128);
}
</style>
